import { useEffect } from 'react';
import { LoanSubType, PropertyDevelopmentType } from '@apply/constants';
import { useBorrowerLoanSubType } from '../../../../queries/BorrowerFields/CoreFields';
import { usePropertyDevelopmentType } from '../../../../queries/PropertyFields/BridgeFields';
import { usePropertyGuidsCache } from '../../queries/usePropertyGuidsCache';

/**
 * Keeps `propertyDevelopmentType` in step with the Bridge loan sub-type.
 *
 * An Acquisition Bridge is always a New Acquisition, so the question is not asked and the value
 * is set here instead. Switching back to an Inventory Loan clears it, because New Acquisition is
 * no longer offered in the dropdown and a value left behind would render as a blank selection.
 *
 * @param propertyGuid - GUID of the property being edited
 * @param isReview     - review renders are read-only and must not write to the cache
 */
export function usePropertyDevelopmentTypeSync(propertyGuid: string, isReview: boolean): void {
    const [loanSubType] = useBorrowerLoanSubType();
    const [propertyDevelopmentType, set_propertyDevelopmentType] = usePropertyDevelopmentType(propertyGuid);

    // The setter no-ops until the property stub exists in the cache, and Property.tsx seeds that
    // stub in a parent effect (which React runs after this one). Depending on the GUID list makes
    // this effect run again once the stub lands.
    const propertyExists = usePropertyGuidsCache().includes(propertyGuid);

    useEffect(() => {
        if (isReview || !propertyExists) return;

        if (loanSubType === LoanSubType.AcquisitionBridge) {
            if (propertyDevelopmentType !== PropertyDevelopmentType.NewAcquisition) {
                set_propertyDevelopmentType(PropertyDevelopmentType.NewAcquisition);
            }
        } else if (propertyDevelopmentType === PropertyDevelopmentType.NewAcquisition) {
            set_propertyDevelopmentType(undefined);
        }
    }, [propertyGuid, propertyExists, loanSubType, propertyDevelopmentType, isReview]);
}
